import { forwardRef, useEffect, useRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { useClickOutside } from '@/hooks/useClickOutside';
import { popoverContentVariants, type PopoverContentProps } from './variants';

export interface PopoverProps extends HTMLAttributes<HTMLDivElement>, PopoverContentProps {
  /** Whether the floating content is visible. */
  open: boolean;
  /** Called on Escape or an outside press. */
  onClose: () => void;
  /** The always-rendered trigger element the content anchors to. */
  anchor: ReactNode;
}

/**
 * Lightweight anchored dropdown — the desktop counterpart to {@link BottomSheet}
 * (no `ui/popover` existed). The content is absolutely positioned beneath the
 * `anchor`; a press anywhere outside the anchor+content closes (document-level
 * listener via {@link useClickOutside}, so it works no matter what z-index the
 * pressed element has), and Escape closes. Not a full floating-ui solution — a
 * simple top-anchored dropdown, which is all the notification bell needs.
 *
 * @example
 * ```tsx
 * <Popover open={open} onClose={close} anchor={<Button>Bell</Button>}>
 *   <Panel />
 * </Popover>
 * ```
 */
const Popover = forwardRef<HTMLDivElement, PopoverProps>(
  ({ open, onClose, anchor, align, className, children, ...props }, ref) => {
    const rootRef = useRef<HTMLDivElement>(null);
    useClickOutside(rootRef, onClose, open);

    useEffect(() => {
      if (!open) return;
      function handleKeyDown(e: KeyboardEvent) {
        if (e.key === 'Escape') onClose();
      }
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [open, onClose]);

    return (
      <div ref={rootRef} className="relative">
        {anchor}
        {open && (
          <div
            ref={ref}
            role="dialog"
            aria-modal="false"
            className={cn(popoverContentVariants({ align }), className)}
            {...props}
          >
            {children}
          </div>
        )}
      </div>
    );
  },
);
Popover.displayName = 'Popover';

export { Popover };
